import { StandardEvents } from '@shopify/events';

const ANCHOR_KEY = '__auraCartFbtAnchor';
const CACHE = new Map();

const cacheKey = (url, productId) =>
  `${url}|${productId}`;

/*
  CART DRAWER = COMPLEMENTARY ONLY.

  Search & Discovery Related products are intentionally
  never requested here.
*/
async function getComplementaryCards(
  baseUrl,
  productId,
  force = false
) {
  const key =
    cacheKey(baseUrl, productId);

  if (force) {
    CACHE.delete(key);
  }

  if (CACHE.has(key)) {
    return CACHE.get(key);
  }

  const request = (async () => {
    const params =
      new URLSearchParams({
        product_id: String(productId),
        limit: '10',
        section_id: 'aura-cart-fbt',
        intent: 'complementary',
      });

    const response = await fetch(
      `${baseUrl}?${params}`,
      {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      }
    );

    if (!response.ok) return [];

    const doc =
      new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

    const payload =
      doc.querySelector(
        '.aura-cart-fbt__payload'
      );

    if (!payload) return [];

    return [
      ...payload.querySelectorAll(
        '.aura-cart-fbt__card'
      ),
    ].map((card) => ({
      productId:
        String(card.dataset.productId || ''),
      html: card.outerHTML,
    }));
  })();

  /*
    Cache the promise itself so multiple drawer instances
    requesting the same product share one request.
  */
  CACHE.set(key, request);

  try {
    return await request;
  } catch (error) {
    CACHE.delete(key);
    throw error;
  }
}

class AuraCartFBT extends HTMLElement {
  #version = 0;
  #pool = [];
  #optimisticIds = new Set();

  connectedCallback() {
    this.refresh();
  }

  disconnectedCallback() {
    this.#version += 1;
  }

  getCartProductIds() {
    return (
      this.dataset.cartProductIds || ''
    )
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  setCartProductIds(ids) {
    this.dataset.cartProductIds =
      [
        ...new Set(
          (ids || [])
            .map((id) =>
              String(id || '')
            )
            .filter(Boolean)
        ),
      ].join(',');
  }

  /*
    Prioritize the most recently added cart product,
    then the remaining cart products.

    Every request is still complementary only.
  */
  getCandidateProductIds() {
    const cartIds =
      this.getCartProductIds();

    if (!cartIds.length) return [];

    const preferred =
      String(
        window[ANCHOR_KEY] ||
        this.dataset.productId ||
        ''
      );

    return [
      ...new Set([
        ...(
          preferred &&
          cartIds.includes(preferred)
            ? [preferred]
            : []
        ),
        ...cartIds,
      ]),
    ];
  }

  async refresh({ force = false } = {}) {
    const version = ++this.#version;

    const candidates =
      this.getCandidateProductIds();

    if (!candidates.length) {
      this.#pool = [];
      this.hideResults();
      return;
    }

    this.setAttribute(
      'aria-busy',
      'true'
    );

    try {
      /*
        Fetch every cart product's complementary pool
        concurrently.

        Cached requests resolve immediately.
      */
      const groups =
        await Promise.all(
          candidates.map((productId) =>
            getComplementaryCards(
              this.dataset.url,
              productId,
              force
            )
          )
        );

      if (version !== this.#version) {
        return;
      }

      const cartIds =
        new Set(
          this.getCartProductIds()
        );

      const seen = new Set();

      this.#pool =
        groups
          .flat()
          .filter((card) => {
            if (
              !card.productId ||
              cartIds.has(card.productId) ||
              seen.has(card.productId)
            ) {
              return false;
            }

            seen.add(card.productId);
            return true;
          });

      this.#render();
    } catch (error) {
      if (version === this.#version) {
        console.warn(
          '[Aura Cart FBT]',
          error
        );
      }
    } finally {
      if (version === this.#version) {
        this.removeAttribute(
          'aria-busy'
        );
      }
    }
  }

  /*
    Hide the clicked recommendation immediately and
    simultaneously prefetch complementary products
    for the newly added cart item.
  */
  optimisticAdd(productId) {
    productId =
      String(productId || '');

    if (!productId) return;

    const cartIds =
      new Set(
        this.getCartProductIds()
      );

    if (!cartIds.has(productId)) {
      this.#optimisticIds.add(productId);
    }

    cartIds.add(productId);

    this.setCartProductIds(
      [...cartIds]
    );

    window[ANCHOR_KEY] = productId;

    /*
      Existing cached pool fills the empty card slot
      immediately.
    */
    this.#render();

    /*
      Warm the new product's complementary cache while
      Shopify completes Add to Cart.
    */
    getComplementaryCards(
      this.dataset.url,
      productId
    ).catch((error) => {
      console.warn(
        '[Aura Cart FBT] Prefetch:',
        error
      );
    });
  }

  undoOptimisticAdd(productId) {
    productId =
      String(productId || '');

    if (
      !this.#optimisticIds.has(productId)
    ) {
      return;
    }

    this.#optimisticIds.delete(
      productId
    );

    const cartIds =
      new Set(
        this.getCartProductIds()
      );

    cartIds.delete(productId);

    this.setCartProductIds(
      [...cartIds]
    );

    this.#render();
  }

  syncCartItems(items) {
    if (!Array.isArray(items)) return;

    this.setCartProductIds(
      items.map(
        (item) => item.product_id
      )
    );

    this.#optimisticIds.clear();

    this.#render();
  }

  #render() {
    const results =
      this.querySelector(
        '[data-fbt-results]'
      );

    if (!results) return;

    const limit = Math.max(
      1,
      parseInt(
        this.dataset.limit || '4',
        10
      ) || 4
    );

    const cartIds =
      new Set(
        this.getCartProductIds()
      );

    const selected =
      this.#pool
        .filter(
          (card) =>
            card.productId &&
            !cartIds.has(card.productId)
        )
        .slice(0, limit);

    if (!selected.length) {
      /*
        Don't disappear while a replacement pool is
        currently loading.
      */
      if (
        !this.hasAttribute('aria-busy')
      ) {
        this.hideResults();
      }

      return;
    }

    results.innerHTML = `
      <div
        class="aura-cart-fbt__payload"
        data-has-products="true"
      >
        ${selected
          .map((card) => card.html)
          .join('')}
      </div>
    `;

    this.hidden = false;
  }

  hideResults() {
    this
      .querySelector(
        '[data-fbt-results]'
      )
      ?.replaceChildren();

    this.hidden = true;
  }
}

if (
  !customElements.get(
    'aura-cart-fbt'
  )
) {
  customElements.define(
    'aura-cart-fbt',
    AuraCartFBT
  );
}

function getAddedProductId(event) {
  if (
    event.action !== 'add' ||
    !(event.target instanceof Element)
  ) {
    return '';
  }

  return String(
    event.target
      .closest('product-form-component')
      ?.dataset.productId || ''
  );
}

document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const addedId =
      getAddedProductId(event);

    const elements = () =>
      document.querySelectorAll(
        'aura-cart-fbt'
      );

    /*
      ADD:
      Remove immediately + begin complementary prefetch.
    */
    if (addedId) {
      window[ANCHOR_KEY] = addedId;

      elements().forEach((element) =>
        element.optimisticAdd?.(addedId)
      );
    }

    const cartUpdate =
      event.promise?.then
        ? event.promise
        : Promise.resolve();

    cartUpdate
      .then((result) => {
        const items =
          result?.detail?.items ||
          event.detail?.items;

        elements().forEach((element) => {
          if (Array.isArray(items)) {
            element.syncCartItems?.(items);
          }

          /*
            Add/update:
            cached complementary data wherever possible.

            Remove:
            force fresh complementary results because a
            removed product may now be eligible again.
          */
          element.refresh?.({
            force:
              event.action === 'remove',
          });
        });
      })
      .catch((error) => {
        if (addedId) {
          elements().forEach((element) =>
            element.undoOptimisticAdd?.(
              addedId
            )
          );
        }

        if (error?.name !== 'AbortError') {
          console.warn(
            '[Aura Cart FBT] Cart update rejected:',
            error
          );
        }
      });
  }
);